"use client";

import type { FormEvent, ReactNode } from "react";
import type { PlaceResult } from "@/lib/api/misc";
import { getKundaliTranslations, type KundaliLang } from "./KundaliFormCard.i18n";
import { KundaliLoadingSteps } from "./KundaliFormCard.loading";
import { FormErrorMessage, KundaliSubmitButton } from "./KundaliFormCard.controls";
import type { SEOIntentConfig } from "@/lib/seo-intents";
import { reportPlanSummaryById } from "@/lib/report-plan-summary";
import type { KundaliFieldErrors } from "./KundaliFormCard.validation";
import type { FieldFunnel } from "./useFieldFunnel";
import { KundaliFormFields } from "./KundaliFormCard.field-groups";
import type { KundaliProgressiveCopy } from "./KundaliFormCard.progressive";
import type { KundaliField } from "@/lib/analytics-kundali";
import { useProgressiveKundaliFormState } from "./useProgressiveKundaliFormState";
import progressiveStyles from "./KundaliFormCard.progressive.module.css";

export type CapturedSEOEntry = {
  config: SEOIntentConfig;
  sourcePath: string;
  /** The SPECIFIC paid report this visitor arrived to buy (the report landing
   * page's `next_report`), when the entry is a report-buying path. Lets the intent
   * banner restore the paid context that the generic intent config would lose —
   * so a seeker who tapped a ₹199 report CTA doesn't land on a generic free-entry screen
   * with no sign of what they came to buy (rules 40/43/46). */
  reportPlanId?: string;
};

type KundaliInputFormProps = {
  containerClassName: string;
  formClassName?: string;
  onSubmit: (event: FormEvent) => void;
  seoEntry: CapturedSEOEntry | null;
  language: KundaliLang;
  setLanguage: (language: KundaliLang) => void;
  formReady: boolean;
  name: string;
  setName: (value: string) => void;
  phone: string;
  setPhone: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  birthDate: string;
  setBirthDate: (value: string) => void;
  birthTime: string;
  setBirthTime: (value: string) => void;
  requireBirthTime: boolean;
  birthPlace: string;
  setBirthPlace: (value: string) => void;
  setSelectedPlace: (place: PlaceResult | null) => void;
  selectedPlace: PlaceResult | null;
  onClearPlace: () => void;
  ambiguousSuggestion: PlaceResult | null;
  fieldFunnel: FieldFunnel;
  activeField: string | null;
  setActiveField: (field: string | null) => void;
  markFormStarted: (field: string) => void;
  fieldErrors: KundaliFieldErrors;
  error: string | null;
  loading: boolean;
  loadingStep: number;
  reassuranceNote?: string;
  deliveryField?: ReactNode;
  submitDisabled?: boolean;
  retryCard?: ReactNode;
  submitLabelOverride?: string;
  t: ReturnType<typeof getKundaliTranslations>;
  progressiveCopy?: KundaliProgressiveCopy;
  source: string;
  sourcePath?: string;
  initialFocusField?: KundaliField;
  showIntentBanner?: boolean;
};

export function KundaliInputForm({
  containerClassName,
  formClassName = "card-sacred space-y-5 p-6 sm:p-8",
  onSubmit,
  seoEntry,
  language,
  setLanguage,
  formReady,
  name,
  setName,
  phone,
  setPhone,
  email,
  setEmail,
  birthDate,
  setBirthDate,
  birthTime,
  setBirthTime,
  requireBirthTime,
  birthPlace,
  setBirthPlace,
  setSelectedPlace,
  selectedPlace,
  onClearPlace,
  ambiguousSuggestion,
  fieldFunnel,
  activeField,
  setActiveField,
  markFormStarted,
  fieldErrors,
  error,
  loading,
  loadingStep,
  reassuranceNote,
  deliveryField,
  submitDisabled,
  retryCard,
  submitLabelOverride,
  t,
  progressiveCopy,
  source,
  sourcePath,
  initialFocusField,
  showIntentBanner = true,
}: KundaliInputFormProps) {
  const { visibleStep, showFinalAction, setProgressiveStep, trackProgressiveAction, submit } =
    useProgressiveKundaliFormState({
      progressiveCopy,
      initialFocusField,
      fieldErrors,
      language,
      source,
      sourcePath,
      phone,
      email,
      submitDisabled,
      onSubmit,
    });

  return (
    <div className={containerClassName}>
      <form onSubmit={submit} noValidate className={formClassName}>
        <fieldset
          disabled={!formReady}
          aria-busy={!formReady}
          className="m-0 min-w-0 border-0 p-0 space-y-5 disabled:opacity-80"
        >
          {showIntentBanner && <SEOIntentBanner seoEntry={seoEntry} />}
          <LanguageToggle language={language} setLanguage={setLanguage} />
          <InputReassurance note={reassuranceNote} />
          <KundaliFormFields
            {...{
              language,
              t,
              progressiveCopy,
              progressiveStep: visibleStep,
              setProgressiveStep,
              name,
              setName,
              phone,
              setPhone,
              email,
              setEmail,
              birthDate,
              setBirthDate,
              birthTime,
              setBirthTime,
              requireBirthTime,
              birthPlace,
              setBirthPlace,
              setSelectedPlace,
              selectedPlace,
              onClearPlace,
              ambiguousSuggestion,
              fieldFunnel,
              activeField,
              setActiveField,
              markFormStarted,
              fieldErrors,
              onProgressiveAction: trackProgressiveAction,
            }}
          />
          <FormErrorMessage error={error} />
          {retryCard}
          {showFinalAction && (
            <div className={progressiveCopy ? progressiveStyles.finalAction : undefined}>
              {deliveryField}
              <KundaliSubmitButton
                loading={loading}
                disabled={submitDisabled}
                label={submitLabelOverride || seoEntry?.config?.ctaLabel || t.submit}
                loadingLabel={t.loading}
              />
              {loading && <KundaliLoadingSteps loadingStep={loadingStep} language={language} />}
              {!progressiveCopy && (
                <p className="text-[11px] text-center" style={{ color: "var(--ish-text-faint)" }}>
                  {t.savedNote}
                </p>
              )}
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}

function InputReassurance({ note }: { note?: string }) {
  if (!note) return null;
  return (
    <p className="rounded-md px-3 py-2 text-center text-[12px]" style={reassuranceStyle}>
      {note}
    </p>
  );
}

const reassuranceStyle = {
  background: "var(--ish-bg)",
  border: "1px solid var(--ish-border)",
  color: "var(--ish-text-muted)",
};

function SEOIntentBanner({ seoEntry }: { seoEntry: CapturedSEOEntry | null }) {
  if (!seoEntry) return null;
  const bannerStyle = {
    background: "var(--ish-accent-light)",
    border: "1px solid var(--ish-accent-border)",
    color: "var(--ish-text-muted)",
  } as const;
  const eyebrowStyle = {
    color: "var(--ish-accent)",
    letterSpacing: "0.12em",
  } as const;

  // Paid-report entry: the seeker tapped a specific report CTA (e.g. ₹199 Career
  // Report) and was routed here to build their chart first. Restore the buying
  // context the generic free-entry hero drops — name the report + its price +
  // make clear checkout opens right here after the chart (rules 40/43/46).
  const report = seoEntry.reportPlanId ? reportPlanSummaryById(seoEntry.reportPlanId) : null;
  if (report) {
    return (
      <div className="rounded-lg p-3.5 text-[13px]" style={bannerStyle}>
        <p
          className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
          style={eyebrowStyle}
        >
          <span aria-hidden>ॐ</span> Unlocking · {report.name} · ₹{report.priceInr}
        </p>
        <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
          {seoEntry.config.prompt}
        </p>
        <p className="mt-1 text-[12px]">
          Enter your birth details below — I build your chart first, then your {report.name} (₹
          {report.priceInr}) opens right here. No separate page, no waiting.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg p-3.5 text-[13px]" style={bannerStyle}>
      <p
        className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
        style={eyebrowStyle}
      >
        <span aria-hidden>ॐ</span> Pandit-ji · Your question
      </p>
      <p className="font-semibold" style={{ color: "var(--ish-dark)" }}>
        {seoEntry.config.prompt}
      </p>
      <p className="mt-1 text-[12px]">
        I&apos;ll read your chart to answer this. Share your birth details below — your question
        stays open and I&apos;ll continue it the moment your kundali is ready.
      </p>
    </div>
  );
}

function LanguageToggle({
  language,
  setLanguage,
}: {
  language: KundaliLang;
  setLanguage: (language: KundaliLang) => void;
}) {
  return (
    <div className="flex justify-center">
      <div
        role="tablist"
        aria-label="Language"
        className="flex p-1 rounded-full"
        style={{ background: "var(--ish-bg)", border: "1px solid var(--ish-border)" }}
      >
        {(["en", "hi"] as KundaliLang[]).map((lang) => (
          <button
            key={lang}
            type="button"
            role="tab"
            aria-selected={language === lang}
            onClick={() => setLanguage(lang)}
            className="px-4 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-200"
            style={{
              color: language === lang ? "#fff" : "var(--ish-text-muted)",
              backgroundColor: language === lang ? "var(--ish-accent)" : "transparent",
            }}
          >
            {lang === "en" ? "English" : "हिंदी"}
          </button>
        ))}
      </div>
    </div>
  );
}
